import type { FetchData } from '@hooks/useDataFetcher';
import { useNavigation } from '@hooks/useNavigation';
import { azureAdProvider } from '@shared/constants';
import { checkPermission } from '@shared/permissions';
import resources from '@shared/resources';
import type { Session } from 'next-auth';
import { signIn, useSession } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import type { KetoRelationship as Tuple } from 'types/pop-authz/models/KetoRelationship';

interface IAccountInformation {
  name: string;
  displayName: string;
}

interface IState {
  image?: string;
  username: string;
  userPermissions: Tuple[];
  info?: IAccountInformation;
}

const authorize = async (
  session: Session,
  fetchData: FetchData,
): Promise<{ permissions: { relation_tuples: Tuple[] } }> => {
  if (!session.upn) {
    throw new Error('Authenticated user UPN is missing or malformed');
  }

  const permissions = await fetchData<{ relation_tuples: Tuple[] }>(
    '/api/auth',
    { subject_id: session.upn?.toLowerCase() },
  );

  return { permissions };
};

export const useAuth = (fetchData: FetchData) => {
  const initialized = useRef(false);
  const isRedirecting = useRef(false);
  const { data: session } = useSession();
  const { currentPath, push } = useNavigation();
  const [state, setState] = useState<IState>({
    userPermissions: [],
    username: session?.user?.email ?? '',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency subset
  useEffect(() => {
    let isCancelled = false;

    // Ref guard prevents duplicate signIn() calls; React 18+ batching makes true races unlikely
    if (session?.error) {
      if (isRedirecting.current) {
        return;
      }

      isRedirecting.current = true;
      signIn(azureAdProvider, { callbackUrl: currentPath });
      return;
    }

    if (!session?.user?.email) {
      return;
    }

    const loadAuthorization = async () => {
      try {
        const { permissions } = await authorize(session, fetchData);

        if (isCancelled) {
          return;
        }

        initialized.current = true;

        setState((state) => ({
          ...state,
          username: session.user?.email ?? '',
          userPermissions: permissions?.relation_tuples ?? [],
          info: {
            name: session.user?.name ?? session.user?.email ?? '',
            displayName: session.user?.name ?? session.user?.email ?? '',
          },
        }));
      } catch (error) {
        if (isCancelled) {
          return;
        }

        resources.logger.error(
          error instanceof Error ? error : 'Authorization bootstrap failed',
          'useAuth/authorize',
        );
      }
    };

    void loadAuthorization();

    return () => {
      isCancelled = true;
    };
  }, [session?.user?.email, session?.error]);

  useEffect(() => {
    if (initialized.current) {
      const isAllowed = checkPermission(currentPath, state.userPermissions);

      if (!isAllowed) {
        push('/404');
      }
    }
  }, [push, currentPath, state.userPermissions]);

  return state;
};
